import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type Locale, useLocale } from 'next-intl';

import { EventType } from '@/analytics/types';
import isBrowser from '@/utils/isBrowser';

interface UseLanguageSwitcherReturn {
  locale: string;
  onLocaleChange: (newLocale: string) => void;
  onLanguageSwitcherClick: () => void;
}

/**
 * Custom hook for language switching
 */
const useLanguageSwitcher = (): UseLanguageSwitcherReturn => {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams.toString();

  function onLanguageSwitcherClick(): void {
    void window.ouraAnalytics.track(EventType.ModuleClicked, {
      action: 'language selected',
      cta: 'language',
      location: 'footer',
    });
  }

  function handleLocaleChange(newLocale: Locale): void {
    if (!isBrowser() || newLocale === locale || !pathname) return;

    const url = `${pathname}${query ? '?' + query : ''}`;
    const newPathname = url.replace(`/${locale}`, `/${newLocale}`);

    try {
      document.cookie = `NEXT_LOCALE=${newLocale}; path=/; SameSite=Lax`;

      // Add small delay for mobile browsers to process cookie
      setTimeout(() => {
        router.push(newPathname);
      }, 0);
    } catch (error) {
      // Fallback: navigate without setting cookie if cookie setting fails
      router.push(newPathname);
    }
  }

  return {
    locale,
    onLocaleChange: handleLocaleChange,
    onLanguageSwitcherClick,
  };
};

export default useLanguageSwitcher;
